import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Card } from '@/components/card/card';
import { DataGrid } from '@/components/data-grid';
import { createColumnHelper } from '@tanstack/react-table';
import { KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import {
  ISubscription,
  BillingCycle,
} from '@/types/subscription-management.types';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { useNavigate } from 'react-router-dom';
import { SubscriptionStatus } from '@/constant';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

interface SubscriptionsListProps {
  onEdit?: (subscription: ISubscription) => void;
  onDelete?: (subscription: ISubscription) => void;
  refreshTrigger?: number;
  canEdit?: boolean;
  canDelete?: boolean;
  canManagePermissions?: boolean;
}

const SubscriptionsList: React.FC<SubscriptionsListProps> = ({
  onEdit,
  onDelete,
  refreshTrigger,
  canEdit = false,
  canDelete = false,
  canManagePermissions = false,
}) => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const [dataKey, setDataKey] = useState(() => Date.now());
  const [statusFilter, setStatusFilter] = useState<number | undefined>(
    undefined
  );
  const getErrorMessage = (error: unknown, fallback: string): string => {
    const e = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return e?.response?.data?.message || e?.message || fallback;
  };

  const {
    totalItems,
    handleTotalItems,
    totalPages,
    handleTotalPages,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
  } = usePagination();

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageSize: 5 }));
  }, [setPagination]);

  const columnHelper = createColumnHelper<ISubscription>();

  // Convert HTML string to plain text by stripping tags and decoding entities
  const toPlainText = (html: unknown): string => {
    if (typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  };

  // Map numeric billing_cycle to user-friendly label
  const billingCycleLabel = (val: unknown): string => {
    const num = typeof val === 'number' ? val : Number(val);
    if (num === BillingCycle.MONTHLY) return 'Monthly';
    if (num === BillingCycle.YEARLY) return 'Yearly';
    if (num === BillingCycle.FIVE_DAYS) return '5 Days';
    return '-';
  };

  const handleEditPermissions = useCallback(
    (subscription: ISubscription) => {
      navigate(`/subscription-management/permissions/${subscription.id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <div className="font-medium text-gray-900">
            {toPlainText(info.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => {
          const price = info.getValue();
          return (
            <div className="text-gray-700">
              {typeof price === 'number' ? formatFinnishCurrency(price) : '-'}
            </div>
          );
        },
      }),
      columnHelper.accessor('billing_cycle', {
        header: 'Billing Cycle',
        cell: (info) => {
          const v = info.getValue();
          const text = billingCycleLabel(v);
          return <div className="text-gray-700">{text}</div>;
        },
      }),
      columnHelper.accessor('is_active', {
        header: 'Status',
        cell: (info) => (
          <span
            className={
              info.getValue()
                ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 contrast:bg-green-900 text-green-800 dark:text-green-200 contrast:text-green-200'
                : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'
            }
          >
            {info.getValue() ? 'Active' : 'Inactive'}
          </span>
        ),
      }),
      columnHelper.accessor('updated_at', {
        header: 'Last Updated',
        cell: (info) => {
          const date = info.getValue();
          return date ? (
            <div className="text-gray-600">
              {formatDisplayDate(new Date(date as string))}
            </div>
          ) : null;
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          return (
            <div className="flex space-x-2">
              {canManagePermissions && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleEditPermissions(info.row.original)}
                  title="Edit Permissions"
                  aria-label={`Edit permissions for ${toPlainText(info.row.original.name)}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="key"
                    className="text-gray-500"
                  />
                </button>
              )}
              {canEdit && onEdit && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Edit ${info.row.original.name}`}
                  onClick={() => onEdit(info.row.original)}
                  title="Edit Subscription"
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}
              {canDelete && onDelete && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  title="Delete Subscription"
                  onClick={() => onDelete(info.row.original)}
                  aria-label={`Delete ${toPlainText(info.row.original.name)}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-red-500"
                  />
                </button>
              )}
            </div>
          );
        },
      }),
    ],
    [
      columnHelper,
      onEdit,
      onDelete,
      handleEditPermissions,
      canEdit,
      canDelete,
      canManagePermissions,
    ]
  );

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await SubscriptionsService.getSubscriptions(
        pagination.pageIndex + 1,
        pagination.pageSize,
        debouncedValue,
        statusFilter
      );

      if (response.subscriptions && Array.isArray(response.subscriptions)) {
        const items: ISubscription[] = response.subscriptions;
        setSubscriptions(items);
        const total = response.totalCount || items.length;
        handleTotalItems(total);
        handleTotalPages(Math.ceil(total / pagination.pageSize));
      } else {
        enqueueSnackbar('Invalid data format received from server', {
          variant: 'error',
        });
        setSubscriptions([]);
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to load subscriptions'
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedValue,
    statusFilter,
    enqueueSnackbar,
    handleTotalItems,
    handleTotalPages,
  ]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchSubscriptions();
    }
  }, [refreshTrigger, fetchSubscriptions]);

  const fetchRef = useRef(fetchSubscriptions);
  useEffect(() => {
    fetchRef.current = fetchSubscriptions;
  }, [fetchSubscriptions]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRef.current();
        setDataKey(Date.now());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <Card className="subscription-listings overflow-hidden border-1 shadow-[0px 3px 4px 0px rgba(0, 0, 0, 0.03)] card border-gray-200 border rounded-[0.75rem]">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="card-title text-lg font-semibold text-gray-900">
          Subscriptions
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="flex-shrink-0"
            role="search"
            aria-label="Filter subscriptions by status"
          >
            <select
              value={statusFilter ?? ''}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value ? parseInt(e.target.value, 10) : undefined
                )
              }
              className="select select-sm h-8 sm:h-10 text-sm pr-6"
              aria-label="Filter by subscription status"
              style={{ paddingRight: '2.5rem' }}
            >
              <option value="">All Status</option>
              <option value={SubscriptionStatus.ACTIVE}>Active</option>
              <option value={SubscriptionStatus.INACTIVE}>Inactive</option>
            </select>
          </div>
          <div
            className="flex-shrink-0"
            role="search"
            aria-label="Search subscriptions"
          >
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-subscriptions"
              aria-label="Search subscriptions"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-subscriptions"
                placeholder="Search subscriptions..."
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search subscriptions by name"
                role="searchbox"
              />
            </label>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-4" role="status" aria-live="polite">
          Loading subscriptions...
        </div>
      ) : subscriptions?.length === 0 ? (
        <div className="text-center py-4" role="alert">
          No subscriptions found
        </div>
      ) : (
        <div
          className="subscription-grid-wrapper"
          role="region"
          aria-label="Search results"
        >
          <DataGrid
            key={dataKey}
            columns={columns}
            data={subscriptions}
            loading={loading}
            rowSelect={false}
            aria-label="Subscriptions table"
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems: totalItems,
              totalPages: totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
          />
        </div>
      )}
    </Card>
  );
};

export default React.memo(SubscriptionsList);
